import React, { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronDownOutline } from "react-icons/io5";
import { normalizeUrl, appendSizeToImageUrl } from "../../../../../utils/imageUtils";
import "./style.css";
import { generateProductUrl } from "../../../../../utils/productUrlUtils";
import { getOrderItemPricingMode } from "../../../../../utils/orderPricingMode";

const PLACEHOLDER_IMAGE = "/placeholder-image.svg";

const INR_FORMATTER = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? INR_FORMATTER.format(amount) : "-";
};

const formatDualLanguage = (primary, secondary) => {
  const hasPrimary = Boolean(primary);
  const hasSecondary = Boolean(secondary);

  if (hasPrimary && hasSecondary) {
    if (primary === secondary) {
      return primary;
    }
    return `${primary} (${secondary})`;
  }

  if (hasPrimary) {
    return primary;
  }

  if (hasSecondary) {
    return secondary;
  }

  return "-";
};

const getVariantAttributes = (item) => {
  if (Array.isArray(item?.variant?.skuAttributes) && item.variant.skuAttributes.length) {
    return item.variant.skuAttributes;
  }

  if (Array.isArray(item?.skuAttributes) && item.skuAttributes.length) {
    return item.skuAttributes;
  }

  if (Array.isArray(item?.variants) && item.variants.length) {
    const withAttributes = item.variants.find(
      (variantItem) => Array.isArray(variantItem?.skuAttributes) && variantItem.skuAttributes.length
    );
    if (withAttributes) {
      return withAttributes.skuAttributes;
    }
  }

  return [];
};

const getVariantImage = (item, variantAttributes) => {
  const attributeImage = variantAttributes?.find((attr) => attr?.skuImageUrl)?.skuImageUrl;

  // Priority: variant-specific images first, then product image at index 0
  let imageUrl = (
    attributeImage ||
    item?.variant?.skuImageUrl ||
    item?.variant?.image ||
    item?.image ||
    item?.product?.imgUrl?.[0] || // Always use index 0 from product imgUrl array
    PLACEHOLDER_IMAGE
  );

  // Normalize and optimize image URL for display
  if (imageUrl && imageUrl !== PLACEHOLDER_IMAGE) {
    imageUrl = normalizeUrl(imageUrl);
    imageUrl = appendSizeToImageUrl(imageUrl, '200x200');
  }

  return imageUrl;
};

const getImporterPriceValue = (item, orderType = null) => {
  // Importer Price = finalPrice (or wholesaleFinalPrice for wholesale) * Quantity
  // Priority: Check orderType first, then finalPrice/wholesaleFinalPrice > breakdown.FBP
  const variant = item?.variant;
  const isWholesale = orderType?.toLowerCase() === 'wholesale';
  let price = 0;

  if (isWholesale) {
    // For wholesale orders, use wholesaleFinalPrice
    if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
      price = Number(variant.wholesaleFinalPrice);
    } else {
      const breakdown = variant?.breakdown;
      price = Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
    }
  } else {
    // For dropshipping orders, use finalPrice
    if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
      price = Number(variant.finalPrice);
    } else {
      const breakdown = variant?.breakdown;
      price = Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
    }
  }

  const quantity = Number(item?.quantity);
  const validPrice = Number.isFinite(price) ? price : 0;
  const validQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

  return validPrice * validQuantity;
};

const getImporterQuantity = (item) => {
  const sources = [
    item?.product?.quantity,
    item?.variant?.quantity,
    item?.importerQuantity,
    item?.quantity,
  ];

  for (const source of sources) {
    const value = Number(source);
    if (Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

const getPricingMode = (item, orderBreakdown, orderType = null, isManualOrder = false) =>
  getOrderItemPricingMode(item, {
    orderBreakdown,
    orderType,
    isManualOrder,
    dropshippingLabel: "dropshipping",
  });

const openProductPDP = (variation, offerId) => {
  if (!variation || variation.isFailed) return;
  const isAli = Boolean(offerId);
  const url = generateProductUrl({
    id: variation.product?._id,
    offer: offerId,
    isAli,
    title: variation.product?.title || variation.productTitle || variation.name || '-',
    variantId: variation.variant?._id || '0',
  });
  window.open(url, '_blank');
};

export default function ProductOrderTable({ data }) {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState({});

  // Calculate shipping charges
  const shippingCharges = Number(data?.totalShippingCharges) || 0;
  const imPointsDiscount = Number(data?.imPointsDiscount) || 0;

  const orderItems = useMemo(
    () => (Array.isArray(data?.orderDetails) ? data.orderDetails : []),
    [data?.orderDetails]
  );

  const rows = useMemo(
    () => {
      if (!Array.isArray(orderItems) || orderItems.length === 0) {
        return [];
      }

      // Group items by offer ID (product SKU)
      const groupedByOfferId = orderItems.reduce((acc, item) => {
        const offerId = item?.product?.sku || item?.sku || item?.orderSku || 'no-offer-id';
        if (!acc[offerId]) {
          acc[offerId] = [];
        }
        acc[offerId].push(item);
        return acc;
      }, {});

      // For Shopify orders detection
      const isShopifyOrder = data?.marketplace === 'shopify';
      const shopifyTotalConsumerPrice = isShopifyOrder
        ? orderItems.reduce((sum, item) => sum + (Number(item?.consumerPrice) || 0) * (Number(item?.quantity) || 0), 0)
        : 0;

      // Build a map of productId -> wholesale FBP (total discounted price for the product) from breakdown
      const wholesaleBreakdownMap = new Map();
      if (Array.isArray(data?.breakdown)) {
        for (const breakdownItem of data.breakdown) {
          if (breakdownItem?.pricingMode === 'wholesale' && breakdownItem?.productId && breakdownItem?.breakdown?.[0]?.FBP) {
            wholesaleBreakdownMap.set(breakdownItem.productId.toString(), Number(breakdownItem.breakdown[0].FBP) || 0);
          }
        }
      }

      // Transform grouped data into rows
      return Object.entries(groupedByOfferId).map(([offerId, items], groupIndex) => {
        // Get the first item for common product data
        const firstItem = items[0];
        const variantAttributes = getVariantAttributes(firstItem);
        const variantImage = getVariantImage(firstItem, variantAttributes);

        // Calculate totals
        const totalQuantity = items.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);

        // Check if this product group is wholesale
        const productId = firstItem?.product?._id?.toString() || firstItem?.product?.toString() || '';
        const wholesaleFBP = wholesaleBreakdownMap.get(productId);
        const isWholesale = !!wholesaleFBP;

        // Check if this is a manual order (marketplace === 'manual' or createdVia === 'manual_admin')
        const isManualOrder = data?.marketplace?.toLowerCase() === 'manual' || data?.createdVia === 'manual_admin';

        // Check order type (only for manual orders)
        const orderType = isManualOrder ? (data?.orderType?.toLowerCase()) : null;
        const isOrderWholesale = isManualOrder && orderType === 'wholesale';

        // Helper function to get price - use wholesaleFinalPrice for wholesale, finalPrice for dropshipping
        const getItemPrice = (item, pricingMode = null) => {
          const variant = item?.variant;
          const itemMode = pricingMode || getPricingMode(item, data?.breakdown || [], isManualOrder ? data?.orderType : null, isManualOrder);
          const isItemWholesale = itemMode === 'wholesale' || (isManualOrder && isOrderWholesale);

          if (isItemWholesale) {
            if (item?.consumerPrice != null && Number(item.consumerPrice) > 0) {
              return Number(item.consumerPrice);
            } else {
              // For wholesale, use wholesaleFinalPrice
              if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
                return Number(variant.wholesaleFinalPrice);
              }
              // Fallback to breakdown FBP
              const breakdown = variant?.breakdown;
              return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
            }
          } else {
            if (item?.consumerPrice != null && Number(item.consumerPrice) > 0) {
              return Number(item.consumerPrice);
            } else {
              // For dropshipping, use finalPrice
              if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
                return Number(variant.finalPrice);
              }
              const breakdown = variant?.priceBreakdown || variant?.breakdown;
              return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
            }
          }
        };

        // Calculate pricing mode for each item and separate dropshipping and wholesale
        let dropshippingTotal = 0;
        let wholesaleTotal = 0;
        let wholesaleQuantity = 0;

        items.forEach(item => {
          // Skip failed products in price calculation
          if (item?.isFailed) {
            return;
          }

          const itemPricingMode = getPricingMode(item, data?.breakdown || [], isManualOrder ? data?.orderType : null, isManualOrder);
          const quantity = Number(item?.quantity) || 0;

          if (itemPricingMode === 'wholesale') {
            wholesaleQuantity += quantity;
            // Calculate wholesale total from individual items
            const price = getItemPrice(item, itemPricingMode);
            wholesaleTotal += price * quantity;
          } else {
            const price = getItemPrice(item, itemPricingMode);
            dropshippingTotal += price * quantity;
          }
        });

        // If manual order type is wholesale and we have wholesale items, use calculated total
        // Otherwise, use breakdown FBP if available
        if (isManualOrder && isOrderWholesale && wholesaleQuantity > 0 && wholesaleTotal > 0) {
          // Already calculated above
        } else if (isWholesale && wholesaleFBP && wholesaleQuantity > 0) {
          wholesaleTotal = wholesaleFBP;
        }

        // Calculate per-unit FBP for wholesale products (for variant table display)
        const wholesaleFBPPerUnit = wholesaleQuantity > 0 && wholesaleTotal > 0
          ? wholesaleTotal / wholesaleQuantity
          : 0;

        // Total importer price = dropshipping + wholesale
        const totalImporterPrice = dropshippingTotal + wholesaleTotal;

        // Check if product has both types
        const hasDropshipping = dropshippingTotal > 0;
        const hasWholesale = wholesaleTotal > 0;
        const hasBothTypes = hasDropshipping && hasWholesale;
        // Calculate total consumer price from mkp_current_total_price
        // For Shopify: distribute mkp_current_total_price proportionally
        // For others: sum of (consumerPrice × quantity) for all items in the group
        let totalConsumerPrice = 0;
        const mkpTotal = Number(data?.mkp_current_total_price) || 0;
        const totalConsumerPriceFromItems = orderItems.reduce((sum, item) =>
          sum + (Number(item?.consumerPrice) || 0) * (Number(item?.quantity) || 0), 0);

        if (isShopifyOrder && totalConsumerPriceFromItems > 0 && mkpTotal > 0) {
          // For Shopify: distribute mkp_current_total_price proportionally based on consumerPrice
          const groupConsumerTotal = items.reduce((sum, item) =>
            sum + (Number(item?.consumerPrice) || 0) * (Number(item?.quantity) || 0), 0);
          totalConsumerPrice = (groupConsumerTotal / totalConsumerPriceFromItems) * mkpTotal;
        } else {
          // For non-Shopify: sum of (consumerPrice × quantity) for all items in the group
          totalConsumerPrice = items.reduce((sum, item) =>
            sum + (Number(item?.consumerPrice) || 0) * (Number(item?.quantity) || 0), 0);
        }

        const totalImporterQuantity = items.reduce((sum, item) => {
          const qty = getImporterQuantity(item);
          return sum + (qty || 0);
        }, 0);

        // Calculate pricing mode using the first item (same logic as seller panel)
        const pricingMode = getPricingMode(firstItem, data?.breakdown || [], isManualOrder ? data?.orderType : null, isManualOrder);

        return {
          rowId: `offer-${offerId}-${groupIndex}`,
          offerId: offerId === 'no-offer-id' ? '-' : offerId,
          productTitle: firstItem?.isFailed
            ? (firstItem?.productName || firstItem?.name || "Failed Product")
            : (firstItem?.product?.title || firstItem?.productTitle || firstItem?.name || "-"),
          hasFailedItems: items.some(item => item?.isFailed),
          variantImage,
          totalQuantity,
          totalImporterPrice,
          totalConsumerPrice,
          totalImporterQuantity,
          pricingMode,
          hasDropshipping,
          hasWholesale,
          hasBothTypes,
          carrierName: firstItem?.carrierName,
          trackingId: firstItem?.trackingId,
          shippingMethod: firstItem?.shippingMethod,
          // Get orderItemId from items - show unique values only
          orderItemId: (() => {
            const orderItemIds = items.map(item => item?.orderItemId).filter(Boolean);
            const uniqueIds = [...new Set(orderItemIds)];
            return uniqueIds.length > 0 ? uniqueIds.join(', ') : '-';
          })(),
          // Store all variations for this product
          variations: items.map((item) => {
            const itemVariantAttributes = getVariantAttributes(item);
            const itemVariantImage = getVariantImage(item, itemVariantAttributes);
            const variant = item?.variant;

            // Calculate pricing mode for this individual item
            const itemPricingMode = getPricingMode(item, data?.breakdown || [], isManualOrder ? data?.orderType : null, isManualOrder);

            // Calculate importer price per unit: use wholesaleFinalPrice for wholesale, variant.finalPrice for dropshipping
            let importerPricePerUnit = 0;
            if (itemPricingMode === 'dropshipping') {
              if (item?.consumerPrice != null && Number(item.consumerPrice) > 0) {
                importerPricePerUnit = Number(item.consumerPrice);
              } else {
                // Dropshipping: use variant.finalPrice or fallback to breakdown.FBP
                if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
                  importerPricePerUnit = Number(variant.finalPrice);
                } else {
                  // Fallback to breakdown.FBP if finalPrice not available
                  const breakdown = variant?.breakdown;
                  importerPricePerUnit = Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
                }
              }
            } else {
              if (item?.consumerPrice != null && Number(item.consumerPrice) > 0) {
                importerPricePerUnit = Number(item.consumerPrice);
              } else {
                // Wholesale: variation price = row Importerr Price / row total quantity
                if (totalQuantity > 0 && Number(totalImporterPrice) > 0) {
                  importerPricePerUnit = totalImporterPrice / totalQuantity;
                } else if (variant?.wholesaleFinalPrice != null && Number(variant.wholesaleFinalPrice) > 0) {
                  importerPricePerUnit = Number(variant.wholesaleFinalPrice);
                } else {
                  importerPricePerUnit = wholesaleFBPPerUnit;
                }
              }
            }

            // Get finalPrice from MongoDB variant (for reference, but use importerPricePerUnit for display)
            const finalPriceFromMongo = variant?.finalPrice != null ? Number(variant.finalPrice) : null;

            return {
              ...item,
              variantAttributes: itemVariantAttributes,
              variantImage: itemVariantImage,
              consumerPriceValue: item?.consumerPrice,
              importerPriceValue: item?.isFailed ? 0 : importerPricePerUnit, // Show 0 for failed products
              finalPriceValue: item?.isFailed ? 0 : importerPricePerUnit, // Show 0 for failed products
              sku: item?.sku || item?.variant?.skuId || item?.variant?.sku || '-',
              orderItemId: item?.orderItemId || '-',
              pricingMode: itemPricingMode,
              isFailed: item?.isFailed || false,
              error: item?.error || '',
              failureType: item?.failureType || '',
              productName: item?.productName || item?.name || '',
            };
          }),
        };
      });
    },
    [orderItems, data?.marketplace, data?.mkp_current_total_price, data?.breakdown]
  );

  const toggleExpandRow = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Check if marketplace is Amazon
  const isAmazonMarketplace = data?.marketplace?.toLowerCase()?.includes('amazon');

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: "variantImage",
          label: "Image",
          width: 48,
          render: (value, row) => (
            <div className="flex justify-center">
              <img
                src={value || PLACEHOLDER_IMAGE}
                alt={row?.productTitle || "Product"}
                className="order-table-product-img object-cover rounded border border-gray-200 cursor-pointer"
                referrerPolicy="no-referrer"
                onClick={(e) => {
                  e.stopPropagation();
                  const firstVariation = row.variations?.[0];
                  if (firstVariation) {
                    openProductPDP(firstVariation, row?.offerId);
                  }
                }}
                onError={(event) => {
                  if (event?.target?.src !== PLACEHOLDER_IMAGE) {
                    event.target.src = PLACEHOLDER_IMAGE;
                  }
                }}
              />
            </div>
          ),
        },

        {
          key: "productTitle",
          label: "Name",
          width: 160,
          render: (value, row) => {
            const hasFailed = row?.hasFailedItems || row?.variations?.some(v => v?.isFailed);
            return (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  className="text-left text-blue-600 hover:text-blue-800 whitespace-normal break-words"
                  title={value}
                  onClick={(e) => {
                    e.stopPropagation();
                    const firstVariation = row.variations?.[0];
                    if (firstVariation && !firstVariation?.isFailed) {
                      openProductPDP(firstVariation, row?.offerId);
                    }
                  }}
                >
                  {value || "-"}
                </button>
                {hasFailed && (
                  <span className="text-red-500 text-xs">
                    ⚠️ Contains failed items
                  </span>
                )}
              </div>
            );
          },
        },
        {
          key: "offerId",
          label: "Offer ID",
          width: 90,
          render: (value, row) => {
            const isExpanded = expandedRows[row.rowId];
            return (
              <div className="flex flex-col items-start gap-1">
                <button
                  type="button"
                  className="offer-id-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const firstVariation = row.variations?.[0];
                    if (firstVariation) {
                      openProductPDP(firstVariation, row?.offerId);
                    }
                  }}
                >
                  {value || "-"}
                </button>
                <button
                  type="button"
                  className="variant-toggle-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandRow(row.rowId);
                  }}
                  title={isExpanded ? 'Hide variation details' : 'Show variation details'}
                >
                  <IoChevronDownOutline
                    className={`variant-toggle-icon ${isExpanded ? 'expanded' : ''}`}
                    size={12}
                  />
                </button>
              </div>
            );
          },
        },
        {
          key: "totalConsumerPrice",
          label: "Seller Price",
          width: 95,
          render: (value) => formatCurrency(value),
        },
      ];

      // Add orderItemId column only for Amazon marketplace
      if (isAmazonMarketplace) {
        baseColumns.push({
          key: "orderItemId",
          label: "Order Item ID",
          width: 100,
          render: (value) => value || "-",
        });
      }

      // Add remaining columns
      baseColumns.push(
        {
          key: "totalQuantity",
          label: "Order Qty",
          width: 72,
          render: (value) => (Number.isFinite(Number(value)) ? Number(value) : "-"),
        },
        {
          key: "totalImporterPrice",
          label: "Importerr Price",
          width: 95,
          render: (value) => (Number.isFinite(Number(value)) ? formatCurrency(value) : "-"),
        },
        {
          key: "totalImporterQuantity",
          label: "Importerr Qty",
          width: 72,
          render: (value) => (Number.isFinite(Number(value)) ? Number(value) : "-"),
        },
        {
          key: "pricingMode",
          label: "Type",
          width: 88,
          render: (value, row) => {
            if (row.hasBothTypes) {
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Wholesale
                  </span>
                  <span style={{ margin: '0 2px' }}>+</span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    1-Piece
                  </span>
                </span>
              );
            }
            return (
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${value === 'wholesale'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
                  }`}
              >
                {value === 'wholesale' ? 'Wholesale' : '1-Piece'}
              </span>
            );
          },
        },
        {
          key: "trackingId",
          label: "Tracking",
          width: 100,
          render: (value) => value || "-",
        },
        {
          key: "shippingMethod",
          label: "Shipping",
          width: 90,
          render: (value) => value || "-",
        },
        {
          key: "carrierName",
          label: "Carrier",
          width: 90,
          render: (value) => value || "-",
        }
      );

      return baseColumns;
    },
    [expandedRows, toggleExpandRow, isAmazonMarketplace]
  );

  // Logic for Payment Breakdown (Same as OrderInfo)
  const pb = data?.paymentBreakdown;
  const hasNewBreakdown = pb && typeof pb === 'object';
  const breakdownSum = (
    (Number(pb?.imPoints?.amount) || 0) +
    (Number(pb?.wallet?.amount) || 0) +
    (Number(pb?.razorpay?.amount) || 0) +
    (Number(pb?.ccavenue?.amount) || 0) +
    (Number(pb?.bankTransfer?.amount) || 0)
  );

  const productTotal = rows.reduce((sum, row) => sum + (row.totalImporterPrice || 0), 0);
  const totalWithShipping = productTotal + shippingCharges;

  const totalOrderValue = data?.grossOrderAmount ||
    (hasNewBreakdown && breakdownSum > 0 ? breakdownSum : totalWithShipping);

  const displaySubtotal = Math.max(0, totalOrderValue - shippingCharges);

  const getPaymentSource = (order) => {
    if (order?.razorpayPaymentId || order?.razorpayOrderId) return "Razorpay";
    if (order?.ccavenueOrderId || order?.ccavenueTrackingId) return "CCAvenue";
    if (order?.bankTransferTxnId || order?.paymentProofUrl) return "Bank Transfer";
    if (order?.transactionId) return "IM Cash";
    return "—";
  };

  const imPointsAmount = hasNewBreakdown ? (Number(pb.imPoints?.amount) || 0) : imPointsDiscount;
  const walletAmount = hasNewBreakdown ? (Number(pb.wallet?.amount) || 0) : 0;
  const razorpayAmount = hasNewBreakdown ? (Number(pb.razorpay?.amount) || 0) : 0;
  const ccavenueAmount = hasNewBreakdown ? (Number(pb.ccavenue?.amount) || 0) : 0;
  const bankTransferAmount = hasNewBreakdown ? (Number(pb.bankTransfer?.amount) || 0) : 0;

  const hasWallet = hasNewBreakdown ? walletAmount > 0 : (data?.transactionId && !(data?.razorpayPaymentId || data?.razorpayOrderId));
  const hasRazorpay = hasNewBreakdown ? razorpayAmount > 0 : !!(data?.razorpayPaymentId || data?.razorpayOrderId);
  const hasBankTransfer = hasNewBreakdown ? bankTransferAmount > 0 : !!(data?.bankTransferTxnId || data?.paymentProofUrl);
  const hasCCAvenue = hasNewBreakdown ? ccavenueAmount > 0 : !!(data?.ccavenueOrderId || data?.ccavenueTrackingId);
  const paymentSource = getPaymentSource(data);
  const finalAmount = totalWithShipping - imPointsDiscount;

  return (
    <div className="order_detail_products_table">
      <div className="order-table-wrapper">
        <table className="order-details-table">
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} style={{ width: col.width, minWidth: col.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ width: col.width, minWidth: col.width }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExpanded = expandedRows[row.rowId];
              return (
                <React.Fragment key={row.rowId}>
                  <tr className="order-row">
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={col.key === 'productTitle' ? 'name-cell' : ''}
                        onClick={(e) => {
                          if (col.key === 'offerId') {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="variant-details-row">
                      <td colSpan={columns.length}>
                        <div className="variant-details-content">
                          <table className="variant-details-table">
                            <thead>
                              <tr>
                                <th className="variant-col-image">Image</th>
                                <th className="variant-col-sku">SKU</th>
                                {isAmazonMarketplace && <th>Order Item ID</th>}
                                <th>Attributes</th>
                                <th>Price</th>
                                <th>Type</th>
                                <th>Quantity</th>
                                <th>Supplier Order</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.variations && row.variations.length > 0 ? (
                                row.variations.map((variation, idx) => (
                                  <tr key={`variation-${idx}-${variation._id || variation.orderItemId || idx}`}>
                                    <td className="variant-cell-image">
                                      <img
                                        src={variation.variantImage || PLACEHOLDER_IMAGE}
                                        alt="Variant"
                                        className="variant-table-image"
                                        referrerPolicy="no-referrer"
                                        onError={(e) => {
                                          if (e?.target?.src !== PLACEHOLDER_IMAGE) {
                                            e.target.src = PLACEHOLDER_IMAGE;
                                          }
                                        }}
                                      />
                                    </td>
                                    <td className="variant-cell-sku">{variation.sku || "-"}</td>
                                    {isAmazonMarketplace && <td>{variation.orderItemId || "-"}</td>}
                                    <td>
                                      {variation.isFailed ? (
                                        <div className="flex flex-col gap-1">
                                          <span className="text-red-600 font-medium text-xs">
                                            {variation.productName || 'Product'}
                                          </span>
                                          <span className="text-red-500 text-xs" title={variation.error}>
                                            ⚠️ {variation.error || 'Product failed validation'}
                                          </span>
                                        </div>
                                      ) : (
                                        variation.variantAttributes && variation.variantAttributes.length > 0 ? (
                                          <div className="variant-attributes-inline-table">
                                            {variation.variantAttributes.map((attr, attrIdx) => (
                                              <span key={`${attr?.attributeId || attrIdx}-${attrIdx}`} className="variant-attribute-badge">
                                                <strong>{formatDualLanguage(attr?.attributeNameTrans, attr?.attributeName)}:</strong>{' '}
                                                {formatDualLanguage(attr?.valueTrans, attr?.value)}
                                              </span>
                                            ))}
                                          </div>
                                        ) : (
                                          "-"
                                        )
                                      )}
                                    </td>
                                    <td>
                                      {variation.isFailed ? (
                                        <span className="text-red-600 font-medium">₹0.00</span>
                                      ) : (
                                        variation.finalPriceValue != null && Number.isFinite(Number(variation.finalPriceValue))
                                          ? formatCurrency(variation.finalPriceValue)
                                          : "-"
                                      )}
                                    </td>
                                    <td>
                                      {variation.isFailed ? (
                                        <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                          Failed
                                        </span>
                                      ) : (
                                        <span
                                          className={`px-2 py-1 rounded text-xs font-medium ${variation.pricingMode === 'wholesale'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-blue-100 text-blue-800'
                                            }`}
                                        >
                                          {variation.pricingMode === 'wholesale' ? 'Wholesale' : '1-Piece'}
                                        </span>
                                      )}
                                    </td>
                                    <td>{variation.quantity || "-"}</td>
                                    <td>
                                      {variation.variant?.supplierOrderId ? (
                                        <div className="supplier-order-info">
                                          <span className="supplier-order-id" title={variation.variant.supplierOrderId}>
                                            {variation.variant.supplierOrderId}
                                          </span>
                                          {variation.variant.supplierOrderDate && (
                                            <span className="supplier-order-date">
                                              {new Date(variation.variant.supplierOrderDate).toLocaleDateString('en-IN', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                              })}
                                            </span>
                                          )}
                                        </div>
                                      ) : "-"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={isAmazonMarketplace ? 8 : 7} style={{ textAlign: 'center', padding: '12px' }}>No variations found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invoice-style Summary Box - Right aligned below table */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '20px',
        paddingRight: '16px'
      }}>
        <div className="order-summary-box" style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          minWidth: '320px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: '0.938rem',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#1f2937',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '10px'
          }}>
            Order Summary
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Subtotal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '0.813rem' }}>Subtotal ({orderItems.length} items)</span>
              <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.875rem' }}>
                {formatCurrency(displaySubtotal)}
              </span>
            </div>

            {/* Shipping Charges */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#6b7280', fontSize: '0.813rem' }}>Shipping Charges</span>
              <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.875rem' }}>
                {formatCurrency(shippingCharges)}
              </span>
            </div>

            {/* Divider if breakdown exists */}
            {(imPointsAmount > 0 || hasWallet || hasRazorpay || hasCCAvenue || hasBankTransfer) && (
              <div style={{ borderTop: '1px dashed #e5e7eb', margin: '4px 0' }}></div>
            )}

            {/* Payment Breakdown items */}
            {imPointsAmount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>IM Points</span>
                <span style={{ color: '#059669', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(imPointsAmount)}</span>
              </div>
            )}

            {hasWallet && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>IM Cash</span>
                <span style={{ color: '#7c3aed', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(hasNewBreakdown ? walletAmount : finalAmount)}</span>
              </div>
            )}

            {hasRazorpay && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>Razorpay</span>
                <span style={{ color: '#2563eb', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(hasNewBreakdown ? razorpayAmount : finalAmount)}</span>
              </div>
            )}

            {hasCCAvenue && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>CCAvenue</span>
                <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(hasNewBreakdown ? ccavenueAmount : finalAmount)}</span>
              </div>
            )}

            {hasBankTransfer && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>Bank Transfer</span>
                <span style={{ color: '#b45309', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(hasNewBreakdown ? bankTransferAmount : finalAmount)}</span>
              </div>
            )}

            {!hasWallet && !hasRazorpay && !hasCCAvenue && !hasBankTransfer && paymentSource && paymentSource !== "—" && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#4b5563', fontSize: '13px', fontWeight: '500' }}>{paymentSource}</span>
                <span style={{ color: '#4b5563', fontWeight: '600', fontSize: '13px' }}>{formatCurrency(finalAmount)}</span>
              </div>
            )}

            {/* Grand Total Green Box */}
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #dcfce7',
              margin: '8px -8px -8px -8px'
            }}>
              <span style={{ fontWeight: '700', color: '#166534', fontSize: '14px' }}>Grand Total Amount</span>
              <span style={{ fontWeight: '800', color: '#166534', fontSize: '18px' }}>{formatCurrency(totalOrderValue)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
